import React, { Component } from 'react';
import { PlantDto, VoidFunc } from '@trucktrax/trucktrax-ts-common';
import { Button, postGISToGoogleMapsPoints, vars } from '@trucktrax/trucktrax-common';
import cx from 'classnames';
import { isEqual } from 'lodash';
import messagingConstants from '../../../../constants/messagingConstants';
import { NO_ADDITIONAL_FUNCTIONS_AVAILABLE } from '../../../../constants/appConstants';
import icons from '../icons';
import TTMarker from './TTMarker';
import styles from '../TTMap.module.css';
import MarkerLabel from './MarkerLabel';
import { isValidLatLng } from '../../../../util/geoUtil';

/** Shared fill opacity for plant geofence overlays on the live map. */
const GEOFENCE_FILL_OPACITY = 0.25;

export default class PlantMarkerWithInfoPane extends Component<PlantMarkerWithInfoPaneProps> {
  static defaultProps = {
    showGeofence: false,
    markerColor: vars.gray700,
    mapRef: undefined,
    isMessagingEnabled: true,
  };

  markerRef: any;

  geozones: (google.maps.Circle | google.maps.Polygon)[] = [];

  componentDidMount(): void {
    this.syncPlantGeozonesToMap();
  }

  componentDidUpdate(prevProps: Readonly<PlantMarkerWithInfoPaneProps>): void {
    const geozonesChanged = !isEqual(prevProps.plant.geozones, this.props.plant.geozones);
    const showGeofencesChanged = prevProps.showGeofence !== this.props.showGeofence;
    const mapChanged = prevProps.mapRef !== this.props.mapRef;
    const colorChanged = prevProps.markerColor !== this.props.markerColor;

    if (geozonesChanged || showGeofencesChanged || mapChanged || colorChanged) {
      this.syncPlantGeozonesToMap();
    }
  }

  componentWillUnmount(): void {
    this.removePlantGeozonesFromMap();
  }

  /**
   * Rebuild plant geofence overlays from props.
   * Must run in lifecycle methods only — creating google.maps overlays during
   * render stacks copies on every parent re-render and makes fillOpacity look solid.
   */
  syncPlantGeozonesToMap = (): void => {
    this.removePlantGeozonesFromMap();

    const { plant, showGeofence, markerColor, mapRef } = this.props;
    if (!showGeofence || !mapRef || !plant.geozones?.length) {
      return;
    }

    const shapeStyles: google.maps.CircleOptions = {
      fillColor: markerColor,
      fillOpacity: GEOFENCE_FILL_OPACITY,
      strokeColor: markerColor,
      strokeWeight: 2,
      strokeOpacity: 0.8,
      clickable: false,
      zIndex: 1,
    };

    for (let i = 0; i < plant.geozones.length; i += 1) {
      const geozone = plant.geozones[i].zone;
      if (geozone?.circle) {
        const position = {
          lat: geozone?.circle?.center?.latitude ?? 0,
          lng: geozone?.circle?.center?.longitude ?? 0,
        };

        if (position && isValidLatLng(position.lat, position.lng)) {
          this.geozones.push(
            new google.maps.Circle({
              center: position,
              radius: geozone?.circle?.radius,
              map: mapRef,
              ...shapeStyles,
            }),
          );
        }
      } else if (geozone?.polygon) {
        this.geozones.push(
          new google.maps.Polygon({
            paths: postGISToGoogleMapsPoints(
              geozone!.polygon!.points!,
            ) as google.maps.LatLngLiteral[],
            map: mapRef,
            ...shapeStyles,
          }),
        );
      }
    }
  };

  removePlantGeozonesFromMap = (): void => {
    this.geozones.forEach(geozone => {
      geozone.setMap(null);
    });
    this.geozones = [];
  };

  handleMessageAllDriversForPlant = () =>
    this.props.updateToFieldValueWithPlantDrivers(this.props.plant.url || '');

  generatePlantInfoContent = () => {
    // On the Incoming Materials map messaging is disabled (isMessagingEnabled=false):
    // the destination-plant link is geofence-based and most drivers are external, so
    // "Message All Plant's Drivers" doesn't apply. Show a fallback instead. (DEV-3891)
    if (!this.props.isMessagingEnabled) {
      return (
        <div className={styles.plantPaneBtnContainer}>
          <div data-test="plant-infopane-no-functions">{NO_ADDITIONAL_FUNCTIONS_AVAILABLE}</div>
        </div>
      );
    }

    const sendMessageButton = (
      <div className={styles.buttonHover}>
        <Button
          buttonClassName={cx('tt-btn-basic', styles.plantSendMessageBtn)}
          onClick={this.handleMessageAllDriversForPlant}
          iconClassName="icon-messages"
          dataTest="plant-infopane-new-message-button"
          name={messagingConstants.MESSAGE_ALL_PLANTS_DRIVERS}
        />
      </div>
    );

    const buttons = <div className={styles.plantPaneBtns}>{sendMessageButton}</div>;

    return (
      <div className={styles.plantPaneBtnContainer}>
        <div>{buttons}</div>
      </div>
    );
  };

  render() {
    const { plant, markerColor, mapRef, isSelected, ...rest } = this.props;
    if (mapRef && this.markerRef) {
      this.markerRef.setMap(mapRef);
    }
    try {
      const position = {
        lat: plant.location!.latitude ?? 0,
        lng: plant.location!.longitude ?? 0,
      };

      const plantIconAnchorX = 11;
      const plantIconAnchorY = 17;
      // Clears the house icon's peak (it extends ~15px above its own anchor point,
      // asymmetric with the ~4px it extends below) plus the same visual gap the label
      // has on the other side, so it sits just above the icon with an equal-looking gap.
      const plantLabelVerticalOffset = 35;

      const icon = {
        path: icons.housePath,
        fillColor: markerColor,
        fillOpacity: 1,
        strokeWeight: 0,
        // Updated anchor point syntax for compatibility
        anchor: { x: plantIconAnchorX, y: plantIconAnchorY },
      };

      const shadowIcon = {
        ...icon,
        fillColor: 'black',
        fillOpacity: 0.5,
        scale: 1.075,
        strokeWeight: 2,
        strokeColor: 'black',
        strokeOpacity: 0.15,
        anchor: { x: plantIconAnchorX + 0.5, y: plantIconAnchorY - 1 },
      };

      const infoPaneTitle = (
        <div data-test="plant-title" title={plant.name}>
          <span className={cx(styles.titleText, 'nowrap-ellipsis')}>{plant.name}</span>
        </div>
      );
      const label = (plant.name && (
        <MarkerLabel
          color={markerColor}
          name={plant.name}
          containerStyles={{
            transform: `translate(-50%, calc(-100% - ${plantLabelVerticalOffset}px))`,
          }}
        />
      )) as JSX.Element | undefined;

      // pixelOffset improves centering of infoPane around marker
      const pixelOffset: google.maps.Size = {
        width: 1.5,
        height: 7,
        equals: other => other?.width === 1.5 && other.height === 7,
      };

      return (
        <div key={plant.url}>
          <TTMarker
            position={position}
            icon={icon}
            infoPaneTitle={infoPaneTitle}
            infoPaneContent={this.generatePlantInfoContent()}
            shadowIcon={shadowIcon}
            label={label}
            pixelOffset={pixelOffset}
            isSelected={isSelected}
            mapRef={mapRef}
            {...rest}
            isLabelHover
            isPlantMarker
            canHover
          />
        </div>
      );
    } catch (e) {
      // this avoids rendering component on errors
      // to debug, log this exception
      return null;
    }
  }
}

export interface PlantMarkerWithInfoPaneProps {
  plant: PlantDto;
  showGeofence?: boolean;
  markerColor: string;
  mapRef?: any;
  updateToFieldValueWithPlantDrivers: (url: string) => void;
  isMessagingEnabled?: boolean;
  onClick?: VoidFunc;
  onCloseClick?: VoidFunc;
  isSelected?: boolean;
  zIndex?: number;
}
